import * as React from "react";
import {
    Button,
    Checkbox,
    Divider,
    Grid,
    Header,
    Input,
    Label,
    List,
    Modal,
    Segment
} from "semantic-ui-react";
import AceEditor from 'react-ace';
import {ConfigContext} from "../../../../context/ConfigContext";
import 'brace/ext/searchbox'
import {backend} from "../../../../../../xconvert-backend";
import {authentication} from "../../../../../../authentication";
import {ftp} from "../../../../../../xconvert-ftp";
import {AddFileRequest, AddFilesRequest} from "../../../../../../generated-ftp/api";
import {getCurrentExportSimOutput} from "../input/ExportSimPrepareSide";

export interface ExportSimAddFilesToFTPModalProps {
    isOpen: boolean
    onClose: (success: boolean, path: string | null) => void
}

export function ExportSimAddFilesToFTPModal(props: ExportSimAddFilesToFTPModalProps) {
    const context = React.useContext(ConfigContext);

    const [addFilePaths, setAddFilePaths] = React.useState<string[]>(["//ROOT/logenios2customer", "//LOGS/EXPORTED"]);
    const [filePathOptions, setFilePathOptions] = React.useState<string[]>([]);
    const [files, setFiles] = React.useState<any[]>([]);
    const [selectedFiles, setSelectedFiles] = React.useState<any[]>([]);
    const [showFile, setShowFile] = React.useState<any>(null);

    React.useEffect(() => {
        if (getCurrentExportSimOutput()) {
            let files = JSON.parse(getCurrentExportSimOutput()).resultFiles;
            setFiles(files);
            setSelectedFiles(files);
        }
        let currentYear = new Date().getFullYear();
        let currentMonth = (new Date().getMonth() + 1).toString()//.padStart(2, '0');
        let exportedPath = "//LOGS/EXPORTED" + "/" + currentYear + "/" + currentMonth ;
        let paths = ["//ROOT/logenios2customer", exportedPath];
        setAddFilePaths(paths);
        setFilePathOptions(paths);
    }, []);

    async function handleSave() {
        let request = {} as AddFilesRequest;
        request.files = [];
        Object.values(selectedFiles).forEach(file => {
            let fileRequest = {} as AddFileRequest;
            fileRequest.base64Content = file.base64Content;
            fileRequest.fileName = file.fileName;
            fileRequest.encoding = file.encoding;
            request.files.push(fileRequest);
        });

        let auth = backend.withTokenAuthHeader(authentication.token);
        let allSuccess = true;

        for (const path of addFilePaths) {
            let response = (await ftp.ftpApi.addFiles(path, request, context.companyId, auth));
            if (!response.success) {
                allSuccess = false;
            }
        }

        props.onClose(allSuccess, addFilePaths.join(" & "));
    }

    function handleClose() {
        props.onClose(false, null);
    }

    function drawFileSegment(file: any) {
        return (
            <Segment>
                <Checkbox
                    type='checkbox'
                    checked={selectedFiles.find(f => f === file) != null}
                    onChange={() => {
                        if (selectedFiles.find(f => f === file) == null) {
                            setSelectedFiles([...selectedFiles, file]);
                        } else {
                            setSelectedFiles(selectedFiles.filter(f => f !== file));
                        }
                    }}
                />
                <Input
                    style={{width: 200}}
                    placeholder='FileName'
                    value={file.fileName}
                    onChange={e => handleFileNameChange(file, e.target.value)}
                />
                <Button color={isThisFileShown(file)} onClick={() => setShowFile(file)}>show</Button>
            </Segment>
        );
    }

    function isThisFileShown(file: any) {
        if (showFile && showFile.base64Content === file.base64Content) {
            return 'blue';
        } else {
            return 'grey';
        }
    }

    function handleFileNameChange(file: any, newFileName: string) {
        let newFiles = files.map(f => {
            if (f === file) {
                return {...f, fileName: newFileName};
            }
            return f;
        });
        setFiles(newFiles);

        if (selectedFiles.find(f => f === file) != null) {
            let newSelectedFiles = selectedFiles.map(f => {
                if (f === file) {
                    return {...f, fileName: newFileName};
                }
                return f;
            });
            setSelectedFiles(newSelectedFiles);
        }
    }

    function drawFileList() {
        React.useEffect(() => {
            if (showFile == null && files.length > 0) {
                setShowFile(files[0]);
            }
            // eslint-disable-next-line
        }, [files]);
        return (
            <Grid>
                {Object.values(files).map((file, idx) => (
                    <Grid.Row key={idx}>
                        {drawFileSegment(file)}
                    </Grid.Row>
                ))}
            </Grid>
        );
    }

    function showFileString() {
        if (showFile) {
            return atob(showFile.base64Content);
        } else {
            return "";
        }
    }

    function addOrRemovePath(path: string) {
        if (addFilePaths.includes(path)) {
            setAddFilePaths(addFilePaths.filter(f => f !== path));
        } else {
            setAddFilePaths([...addFilePaths, path]);
        }
    }

    function drawPathSelectionCheckBox(path: string) {
        return (
            <p>
                <Checkbox
                    type='checkbox'
                    checked={addFilePaths.includes(path)}
                    onChange={() => addOrRemovePath(path)}
                />{path}
            </p>
        );
    }

    function displayPathOptions() {
        return (
            <List>
                <List.Header>Path Options</List.Header>
                {filePathOptions.map((path, idx) => (
                    <List.Item key={idx}>
                        {drawPathSelectionCheckBox(path)}
                    </List.Item>
                ))}
            </List>
        );
    }

    return (
        <Modal
            open={props.isOpen}
            onClose={handleClose}
            dimmer="blurring"
            size='fullscreen'>
            <Header icon='browser' content='Add File'/>
            <Modal.Content>
                {drawFileList()}
                <Divider/>
                <Label pointing='below'>File Content: </Label>
                <AceEditor
                    theme="monokai"
                    mode="json"
                    value={showFileString()}
                    width="100%"
                    height="800px"
                    readOnly={true}
                />
                {displayPathOptions()}
                <Button disabled={selectedFiles.length === 0} onClick={handleSave}>save</Button>
                <Button onClick={handleClose}>close</Button>
            </Modal.Content>
        </Modal>
    );
}
